import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { EnvironmentService, EnvironmentType } from 'src/app/core/environment/environment.service';
import { SettingsService, SettingType } from 'src/app/core/settings/settings.service';

/**
 * Modal component for creating and editing settings.
 * 
 * Usage:
 * 
 * Create mode (default):
 * ```typescript
 * const modal = this.modalService.open(NewSettingModalComponent, { size: 'lg' });
 * await modal.result;
 * ```
 * 
 * Edit mode:
 * ```typescript
 * const modal = this.modalService.open(NewSettingModalComponent, { size: 'lg' });
 * const settingToEdit = this.settingsService.getSettingType('MySettingName');
 * modal.componentInstance.settingToEdit = settingToEdit;
 * await modal.result;
 * ```
 */
@Component({
  selector: 'dcc-new-setting-modal',
  templateUrl: './new-setting-modal.component.html',
  styleUrls: ['./new-setting-modal.component.scss']
})
export class NewSettingModalComponent implements OnInit {
  @Input() settingToEdit?: SettingType;
  environment: EnvironmentType = 'dev';
  
  settingForm!: FormGroup;
  isSubmitting = false;

  classOptions = ['Fixed', 'Free'];
  dataTypeOptions = ['Boolean', 'String', 'Int32', 'Int64', 'Decimal'];
  categoryOptions = [];
  existingSettingNames: Set<string> = new Set();
  
  get isEditMode(): boolean {
    return !!this.settingToEdit;
  }

  get isEditable(): boolean {
    return this.environment === 'dev';
  }

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private environmentService: EnvironmentService,
    private toastr: ToastrService) {}

  get platformDisplayValue(): string {
    const er = this.settingForm.get('platformER')?.value;
    const hub = this.settingForm.get('platformLytxOneHub')?.value;
    const auxCam = this.settingForm.get('platformAuxCam')?.value;
    // No boxes checked defaults to ER only (backwards compat: the platform field was added later,
    // and legacy settings with no platforms only ever applied to ERs), not "all platforms".
    if (!er && !hub && !auxCam) return 'ER';
    return [er ? 'ER' : null, hub ? 'LytxOneHub' : null, auxCam ? 'AuxCam' : null].filter(Boolean).join(', ');
  }

  get isNumericDataType(): boolean {
    const dataType = this.settingForm.get('dataType')?.value;
    return dataType === 'Int32' || dataType === 'Int64' || dataType === 'Decimal';
  }

  get allowableValues(): FormArray {
    return this.settingForm.get('allowableValues') as FormArray;
  }

  ngOnInit(): void {
    this.environment = this.environmentService.getCurrentEnvironment();
    const allSettings = this.settingsService.getAllSettingTypes();
    this.categoryOptions = [...new Set(allSettings.map(t => t.category))].sort();
    this.existingSettingNames = new Set(
      allSettings
        .filter(s => !this.isEditMode || s.name.toLowerCase() !== this.settingToEdit?.name.toLowerCase())
        .map(s => s.name.toLowerCase())
    );
    
    this.settingForm = this.fb.group({
      name: [
        { value: '', disabled: this.isEditMode },
        [
          Validators.required,
          Validators.pattern(/^[A-Z][a-zA-Z0-9]*$/),
          this.uniqueNameValidator.bind(this)
        ]
      ],
      label: ['', Validators.required],
      category: ['', Validators.required],
      class: ['Free', Validators.required],
      defaultValue: ['', Validators.required],
      dataType: ['String', Validators.required],
      minValue: [''],
      maxValue: [''],
      allowableValues: this.fb.array([]),
      units: [''],
      platformER: [true],
      platformLytxOneHub: [false],
      platformAuxCam: [false]
    });
    
    this.populateFormWithExistingSetting();
  }
  
  private populateFormWithExistingSetting(): void {
    if (!this.settingToEdit) return;
    
    // Note: 'class' property may not be present on all SettingType responses from the API
    // Default to 'Free' if not present
    const settingClass = (this.settingToEdit as any)?.['class'] || 'Free';
    
    // The API serializes HardwarePlatformTypes as verbatim enum member names ("Er"), which don't
    // match the casing we send ("ER"), so match case-insensitively.
    const platforms = (this.settingToEdit.applicableHardwarePlatforms ?? []).map(p => p.toLowerCase());
    this.settingForm.patchValue({
      name: this.settingToEdit.name,
      label: this.settingToEdit.label,
      category: this.settingToEdit.category,
      class: settingClass,
      defaultValue: this.settingToEdit.defaultValue,
      dataType: this.settingToEdit.dataType,
      minValue: this.settingToEdit.minValue || '',
      maxValue: this.settingToEdit.maxValue || '',
      units: this.settingToEdit.units || '',
      platformER: platforms.includes('er'),
      platformLytxOneHub: platforms.includes('lytxonehub'),
      platformAuxCam: platforms.includes('auxcam')
    });
    
    if (this.settingToEdit.allowableValues && this.settingToEdit.allowableValues.length > 0) {
      this.settingToEdit.allowableValues.forEach(av => {
        const valueGroup = this.fb.group({
          label: [av.label, Validators.required],
          value: [av.value, Validators.required]
        });
        this.allowableValues.push(valueGroup);
      });
    }
  }

  async onSubmit(): Promise<void> {
    if (this.settingForm.valid) {
      this.isSubmitting = true;
      try {
        // Use getRawValue() to get all form values including disabled fields
        const rawFormData = this.settingForm.getRawValue();
        const platforms: string[] = [];
        if (rawFormData.platformER) platforms.push('ER');
        if (rawFormData.platformLytxOneHub) platforms.push('LytxOneHub');
        if (rawFormData.platformAuxCam) platforms.push('AuxCam');

        const formData = {
          name: rawFormData.name,
          label: rawFormData.label,
          category: rawFormData.category,
          class: rawFormData.class,
          defaultValue: rawFormData.defaultValue,
          dataType: rawFormData.dataType,
          allowableValues: rawFormData.allowableValues,
          minValue: rawFormData.minValue,
          maxValue: rawFormData.maxValue,
          units: rawFormData.units,
          isLegacy: false,
          isExchangeable: true,
          applicableHardwarePlatforms: platforms.length > 0 ? platforms : undefined
        };
        
        if (this.isEditMode) {
          await this.settingsService.updateSetting(formData);
          this.toastr.success('Updated setting: ' + formData.name);
        } else {
          await this.settingsService.createNewSetting(formData);
          this.toastr.success('Created new setting: ' + formData.name);
        }

        await this.settingsService.initializeSettingTypes();
        this.activeModal.close();
      } catch (error) {
        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} setting:`, error);
        this.toastr.error(`Failed to ${this.isEditMode ? 'update' : 'create'} setting. Please try again.`);
      } finally {
        this.isSubmitting = false;
      }
    } else {
      console.error('Form is invalid');
      this.markFormGroupTouched(this.settingForm);
    }
  }

  onCancel(): void {
    this.settingForm.reset({
      class: 'Free',
      dataType: 'String',
      platformER: true,
      platformLytxOneHub: false,
      platformAuxCam: false
    });
    this.allowableValues.clear();

    this.activeModal.close();
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.settingForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  addAllowableValue(): void {
    const allowableValueGroup = this.fb.group({
      label: ['', Validators.required],
      value: ['', Validators.required]
    });
    this.allowableValues.push(allowableValueGroup);
  }

  removeAllowableValue(index: number): void {
    this.allowableValues.removeAt(index);
  }

  dropAllowableValue(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const control = this.allowableValues.at(event.previousIndex);
    this.allowableValues.removeAt(event.previousIndex);
    this.allowableValues.insert(event.currentIndex, control);
    this.allowableValues.markAsDirty();
  }

  private uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const nameLower = control.value.toLowerCase();
    return this.existingSettingNames.has(nameLower) ? { duplicateName: true } : null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }
}
